import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardConfiguration } from '../../services/dashboardConfigurationService';
import dashboardConfigurationService from '../../services/dashboardConfigurationService';
import useAuthStore from '../../store/authStore';
import NewDashboardCharts from './NewDashboardCharts';
import DynamicWidgetRenderer from './DynamicWidgetRenderer';
import './DynamicDashboardRenderer.css';

interface DynamicDashboardRendererProps {
  selectedUserId?: number;
}

const DynamicDashboardRenderer: React.FC<DynamicDashboardRendererProps> = ({ 
  selectedUserId 
}) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<DashboardConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  useEffect(() => {
    loadUserDashboard();
  }, [selectedUserId, user]);

  const loadUserDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user's dashboards
      const dashboards = await dashboardConfigurationService.getDashboards();
      
      // Find default dashboard or use first one
      const defaultDashboard = dashboards.find((d: DashboardConfiguration) => d.isDefault) || dashboards[0];
      
      if (defaultDashboard && defaultDashboard.id) {
        // Load full dashboard configuration
        const fullDashboard = await dashboardConfigurationService.getDashboard(defaultDashboard.id);
        setDashboard(fullDashboard);
      } else {
        // No dashboard found - will show fallback
        setDashboard(null);
      }
      
    } catch (error: any) {
      console.error('Error loading dashboard:', error);
      setError(error.message || 'Failed to load dashboard');
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDashboard = () => {
    navigate('/dashboard-config');
  };

  const handleEditDashboard = () => {
    if (dashboard?.id) {
      navigate(`/dashboard-config/${dashboard.id}`);
    }
  };

  if (loading) {
    return (
      <div className="dynamic-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dynamic-dashboard-error">
        <h3>⚠️ Dashboard Error</h3>
        <p>{error}</p>
        <div className="error-actions">
          <button onClick={loadUserDashboard} className="retry-btn">
            🔄 Retry
          </button>
          <button onClick={handleCreateDashboard} className="create-btn">
            ➕ Create Dashboard
          </button>
        </div>
      </div>
    );
  }

  // If no dynamic dashboard, show static dashboard with option to upgrade
  if (!dashboard) {
    return (
      <div className="dashboard-fallback">
        <div className="upgrade-banner">
          <div className="upgrade-content">
            <h3>🚀 Upgrade to Dynamic Dashboards</h3>
            <p>Create custom dashboards with your own widgets, KPIs, and layouts.</p>
            <button onClick={handleCreateDashboard} className="upgrade-btn">
              Create Your First Dashboard
            </button>
          </div>
        </div>
        
        {/* Show static dashboard as fallback */}
        <NewDashboardCharts selectedUserId={selectedUserId} />
      </div>
    );
  }

  // Render dynamic dashboard
  const currentTab = dashboard.tabs?.[activeTabIndex];

  return (
    <div className="dynamic-dashboard">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="dashboard-info">
          <h1>{dashboard.name}</h1>
          {dashboard.description && (
            <p className="dashboard-description">{dashboard.description}</p>
          )}
        </div>
        <div className="dashboard-actions">
          <button onClick={handleEditDashboard} className="edit-btn">
            ⚙️ Edit Dashboard
          </button>
          <button onClick={handleCreateDashboard} className="create-btn">
            ➕ New Dashboard
          </button>
        </div>
      </div>

      {/* Dashboard Tabs */}
      {dashboard.tabs && dashboard.tabs.length > 1 && (
        <div className="dashboard-tabs">
          {dashboard.tabs.map((tab, index) => (
            <button
              key={tab.id || index}
              className={`tab-button ${index === activeTabIndex ? 'active' : ''}`}
              onClick={() => setActiveTabIndex(index)}
            >
              <span className="tab-icon">{tab.tabIcon || '📊'}</span>
              <span className="tab-name">{tab.tabName}</span>
              {tab.widgets && (
                <span className="tab-widget-count">{tab.widgets.length}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Tab Content */}
      <div className="dashboard-content">
        {currentTab ? (
          <div className="tab-content">
            {currentTab.tabDescription && (
              <div className="tab-description">
                <p>{currentTab.tabDescription}</p>
              </div>
            )}
            
            {/* Widgets Grid */}
            {currentTab.widgets && currentTab.widgets.length > 0 ? (
              <div className="widgets-grid">
                {currentTab.widgets.map((widget) => (
                  <DynamicWidgetRenderer
                    key={widget.id}
                    widget={widget}
                    selectedUserId={selectedUserId}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-tab">
                <div className="empty-content">
                  <h3>📊 No Widgets Yet</h3>
                  <p>This tab doesn't have any widgets configured.</p>
                  <button onClick={handleEditDashboard} className="add-widgets-btn">
                    Add Widgets
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="empty-dashboard">
            <div className="empty-content">
              <h3>📋 No Tabs Configured</h3>
              <p>This dashboard doesn't have any tabs set up yet.</p>
              <button onClick={handleEditDashboard} className="configure-btn">
                Configure Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicDashboardRenderer;
